import { useEffect, useMemo, useState } from 'react';
import { useParams, useLocation } from 'wouter';
import { formatMoney } from '../../lib/decimalUtils';
import {
  useOrders,
  SALES_STAGE_LABELS,
  SALES_STAGE_TRANSITIONS,
  SOURCE_TYPE_LABELS,
  ORDER_TYPE_LABELS,
  TERMINAL_STAGES,
  type CustomerOrderDto,
  type CrmSalesStageConfigDto,
  type TransitionSalesStageDto,
  type ConfirmHighRiskDto,
} from '../../hooks/useOrders';
import {
  CRM_ORDER_PROJECT_STATUS_LABELS,
  formatEnumLabel,
} from '../../lib/enumLabels';

export function OrderDetail() {
  const params = useParams<{ id: string }>();
  const id = params.id;
  const [, navigate] = useLocation();
  const {
    loading,
    error,
    getOrder,
    transitionStage,
    confirmHighRisk,
    listStageConfig,
  } = useOrders();
  const [order, setOrder] = useState<CustomerOrderDto | null>(null);
  const [stageConfig, setStageConfig] = useState<CrmSalesStageConfigDto | null>(null);
  const [showStageModal, setShowStageModal] = useState(false);
  const [selectedNextStage, setSelectedNextStage] = useState<string>('');
  const [stageReason, setStageReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    getOrder(id)
      .then(setOrder)
      .catch(() => {/* error handled in hook */});
  }, [id, getOrder]);

  useEffect(() => {
    listStageConfig()
      .then(setStageConfig)
      .catch(() => {
        /* fallback labels/transitions below remain safe */
      });
  }, [listStageConfig]);

  const stageLabels = useMemo(() => {
    const labels: Record<string, string> = { ...SALES_STAGE_LABELS };
    for (const stage of stageConfig?.stages ?? []) {
      labels[stage.code] = stage.label;
    }
    return labels;
  }, [stageConfig]);

  const stageTransitions = useMemo(() => {
    const transitions: Record<string, readonly string[]> = { ...SALES_STAGE_TRANSITIONS };
    for (const stage of stageConfig?.stages ?? []) {
      transitions[stage.code] = stage.nextStages;
    }
    return transitions;
  }, [stageConfig]);

  const terminalStages = useMemo(() => {
    const configuredTerminal = new Set<string>();
    for (const stage of stageConfig?.stages ?? []) {
      if (stage.nextStages.length === 0) configuredTerminal.add(stage.code);
    }
    return configuredTerminal.size > 0 ? configuredTerminal : TERMINAL_STAGES;
  }, [stageConfig]);

  const handleTransitionStage = async () => {
    if (!order || !selectedNextStage) return;
    setActionLoading(true);
    setActionError(null);
    try {
      const dto: TransitionSalesStageDto = {
        id: order.id,
        expectedVersion: order.version,
        nextStage: selectedNextStage as TransitionSalesStageDto['nextStage'],
        reason: stageReason || undefined,
      };
      const updated = await transitionStage(dto);
      setOrder(updated);
      setShowStageModal(false);
      setSelectedNextStage('');
      setStageReason('');
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to transition stage');
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmHighRisk = async () => {
    if (!order) return;
    setActionLoading(true);
    setActionError(null);
    try {
      const dto: ConfirmHighRiskDto = {
        id: order.id,
        expectedVersion: order.version,
      };
      const updated = await confirmHighRisk(dto);
      setOrder(updated);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to confirm high risk');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading && !order) {
    return <div className="p-6 text-center text-gray-500">加载中...</div>;
  }

  if (error && !order) {
    return (
      <div className="p-6">
        <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-700">{error}</div>
      </div>
    );
  }

  if (!order) return null;

  const nextStages = stageTransitions[order.salesStage] ?? [];
  const isTerminal = terminalStages.has(order.salesStage);

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/crm/orders')}
          className="text-gray-500 hover:text-gray-700 text-sm"
        >
          ← 返回列表
        </button>
        <h1 className="text-2xl font-semibold text-gray-900">订单详情</h1>
      </div>

      {actionError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
          {actionError}
        </div>
      )}

      {/* Order Info */}
      <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-lg font-medium text-gray-900">{order.title}</h2>
            <p className="text-sm text-gray-500 font-mono mt-1">{order.orderNo}</p>
          </div>
          <div className="flex items-center gap-2">
            {order.isHighRisk && !order.highRiskConfirmed && (
              <button
                onClick={handleConfirmHighRisk}
                disabled={actionLoading}
                className="px-3 py-1.5 bg-orange-500 text-white rounded text-sm font-medium hover:bg-orange-600 disabled:opacity-50"
              >
                确认高风险
              </button>
            )}
            {!isTerminal && nextStages.length > 0 && (
              <button
                onClick={() => setShowStageModal(true)}
                className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700"
              >
                推进阶段
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <span className="text-sm text-gray-500">销售阶段</span>
            <div className="mt-1">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                isTerminal
                  ? order.salesStage === 'lost' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                  : 'bg-blue-100 text-blue-800'
              }`}>
                {stageLabels[order.salesStage] ?? order.salesStage}
              </span>
            </div>
          </div>
          <div>
            <span className="text-sm text-gray-500">订单类型</span>
            <p className="mt-1 text-sm text-gray-900">{ORDER_TYPE_LABELS[order.orderType] ?? order.orderType}</p>
          </div>
          <div>
            <span className="text-sm text-gray-500">来源</span>
            <p className="mt-1 text-sm text-gray-900">
              {order.sourceType ? (SOURCE_TYPE_LABELS[order.sourceType] ?? order.sourceType) : '-'}
            </p>
          </div>
          <div>
            <span className="text-sm text-gray-500">风险状态</span>
            <p className="mt-1 text-sm">
              {order.isHighRisk ? (
                <span className={`${order.highRiskConfirmed ? 'text-yellow-700' : 'text-red-700'} font-medium`}>
                  {order.highRiskConfirmed ? '高风险（已确认）' : '高风险（待确认）'}
                </span>
              ) : (
                <span className="text-gray-500">正常</span>
              )}
            </p>
          </div>
          <div>
            <span className="text-sm text-gray-500">总金额</span>
            <p className="mt-1 text-sm text-gray-900">
              {order.totalAmount != null ? formatMoney(order.totalAmount) : '-'}
            </p>
          </div>
          <div>
            <span className="text-sm text-gray-500">创建时间</span>
            <p className="mt-1 text-sm text-gray-900">
              {new Date(order.createdAt).toLocaleString('zh-CN')}
            </p>
          </div>
        </div>

        {order.notes && (
          <div className="mt-4">
            <span className="text-sm text-gray-500">备注</span>
            <p className="mt-1 text-sm text-gray-900">{order.notes}</p>
          </div>
        )}
      </div>

      {/* Order Projects */}
      {order.projects && order.projects.length > 0 && (
        <div className="bg-white shadow-sm rounded-lg p-6">
          <h3 className="text-base font-medium text-gray-900 mb-4">订单项目</h3>
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="text-left text-xs font-medium text-gray-500 uppercase pb-2">项目名称</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase pb-2">项目编码</th>
                <th className="text-right text-xs font-medium text-gray-500 uppercase pb-2">单价</th>
                <th className="text-right text-xs font-medium text-gray-500 uppercase pb-2">数量</th>
                <th className="text-right text-xs font-medium text-gray-500 uppercase pb-2">小计</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase pb-2">状态</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {order.projects.map(p => (
                <tr key={p.id}>
                  <td className="py-2 text-sm text-gray-900">{p.projectName}</td>
                  <td className="py-2 text-sm text-gray-500 font-mono">{p.projectCode ?? '-'}</td>
                  <td className="py-2 text-sm text-gray-900 text-right">{formatMoney(p.unitPrice)}</td>
                  <td className="py-2 text-sm text-gray-900 text-right">{p.quantity}</td>
                  <td className="py-2 text-sm text-gray-900 text-right font-medium">{formatMoney(p.totalPrice)}</td>
                  <td className="py-2 text-sm text-gray-600">
                    {formatEnumLabel(p.status, CRM_ORDER_PROJECT_STATUS_LABELS, '未识别状态')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Stage Transition Modal */}
      {showStageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-xl" key={`stage-modal-${order.id}-${order.version}`}>
            <h3 className="text-lg font-medium text-gray-900 mb-4">推进销售阶段</h3>
            <p className="text-sm text-gray-500 mb-4">
              当前阶段：<span className="font-medium text-gray-900">{stageLabels[order.salesStage]}</span>
            </p>

            {order.isHighRisk && !order.highRiskConfirmed && selectedNextStage === 'trust_building' && (
              <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-md text-orange-700 text-sm">
                高风险订单进入"信任构建"阶段前必须先确认高风险。
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">选择下一阶段</label>
              <div className="space-y-2">
                {nextStages.map(stage => (
                  <label key={stage} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="nextStage"
                      value={stage}
                      checked={selectedNextStage === stage}
                      onChange={e => setSelectedNextStage(e.target.value)}
                      className="text-blue-600"
                    />
                    <span className={`text-sm ${stage === 'lost' ? 'text-red-600' : 'text-gray-900'}`}>
                      {stageLabels[stage] ?? stage}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">原因（可选）</label>
              <textarea
                value={stageReason}
                onChange={e => setStageReason(e.target.value)}
                rows={3}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                placeholder="请输入阶段变更原因..."
              />
            </div>

            {actionError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
                {actionError}
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowStageModal(false);
                  setSelectedNextStage('');
                  setStageReason('');
                  setActionError(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                取消
              </button>
              <button
                onClick={handleTransitionStage}
                disabled={!selectedNextStage || actionLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {actionLoading ? '处理中...' : '确认推进'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
